import DashboardLayout from "@/components/DashboardLayout";
import { ExportButton } from "@/components/ExportButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Phone, ChevronLeft, ChevronRight, Clock, User, CheckCircle, XCircle, Voicemail, Calendar } from "lucide-react";
import { useState } from "react";

export default function CallRecords() {
  const [selectedPlacement, setSelectedPlacement] = useState<string>("all");
  const [selectedOutcome, setSelectedOutcome] = useState<string>("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data: placements } = trpc.placements.list.useQuery();
  const { data: callsData, isLoading } = trpc.calls.list.useQuery({
    placementId: selectedPlacement !== "all" ? parseInt(selectedPlacement) : undefined,
    outcome: selectedOutcome !== "all" ? selectedOutcome : undefined,
    page,
    pageSize,
  });
  
  // Export data query
  const { data: exportData } = trpc.exports.callRecords.useQuery({
    placementId: selectedPlacement !== "all" ? parseInt(selectedPlacement) : undefined,
  });

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return null;
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (num === 0) return null;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(num);
  };

  const exportColumns = [
    { key: "call_date", header: "Call Date", format: (v: any) => new Date(v).toLocaleString() },
    { key: "account_number", header: "Account Number" },
    { key: "debtor_name", header: "Debtor Name" },
    { key: "placement_name", header: "Placement" },
    { key: "call_outcome", header: "Outcome", format: (v: any) => v.replace(/_/g, ' ') },
    { key: "call_duration_seconds", header: "Duration (seconds)" },
    { key: "is_right_party_contact", header: "RPC", format: (v: any) => v === 1 ? "Yes" : "No" },
    { key: "is_promise_to_pay", header: "PTP", format: (v: any) => v === 1 ? "Yes" : "No" },
    { key: "settlement_offered_amount", header: "Settlement Offered", format: (v: any) => v ? `$${parseFloat(v).toFixed(2)}` : "" },
    { key: "settlement_accepted_amount", header: "Settlement Accepted", format: (v: any) => v ? `$${parseFloat(v).toFixed(2)}` : "" },
    { key: "notes", header: "Notes" },
  ];

  const placementData = Array.isArray(placements) ? placements : [];
  const calls = (callsData as any)?.records || [];
  const total = callsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const outcomeIcons: Record<string, React.ReactNode> = {
    'no_answer': <Phone className="h-4 w-4 text-gray-500" />,
    'voicemail': <Voicemail className="h-4 w-4 text-blue-500" />,
    'wrong_number': <XCircle className="h-4 w-4 text-red-500" />,
    'right_party_contact': <User className="h-4 w-4 text-emerald-500" />,
    'promise_to_pay': <CheckCircle className="h-4 w-4 text-amber-500" />,
    'settlement_accepted': <CheckCircle className="h-4 w-4 text-green-500" />,
    'payment_plan_setup': <Calendar className="h-4 w-4 text-purple-500" />,
    'refused': <XCircle className="h-4 w-4 text-red-400" />,
    'callback_scheduled': <Clock className="h-4 w-4 text-cyan-500" />,
  };

  const outcomeColors: Record<string, string> = {
    'no_answer': 'border-l-gray-500',
    'voicemail': 'border-l-blue-500',
    'wrong_number': 'border-l-red-500',
    'right_party_contact': 'border-l-emerald-500',
    'promise_to_pay': 'border-l-amber-500',
    'settlement_accepted': 'border-l-green-500',
    'payment_plan_setup': 'border-l-purple-500',
    'refused': 'border-l-red-400',
    'callback_scheduled': 'border-l-cyan-500',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Call Records</h1>
            <p className="text-muted-foreground">AI agent call activity timeline</p>
          </div>
          <ExportButton
            data={Array.isArray(exportData) ? exportData : []}
            filename="call_records_export"
            columns={exportColumns}
          />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <Select value={selectedPlacement} onValueChange={(v) => { setSelectedPlacement(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Placements" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Placements</SelectItem>
                  {placementData.map((p: any) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.placement_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedOutcome} onValueChange={(v) => { setSelectedOutcome(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Outcomes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Outcomes</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="voicemail">Voicemail</SelectItem>
                  <SelectItem value="right_party_contact">Right Party Contact</SelectItem>
                  <SelectItem value="promise_to_pay">Promise to Pay</SelectItem>
                  <SelectItem value="settlement_accepted">Settlement Accepted</SelectItem>
                  <SelectItem value="payment_plan_setup">Payment Plan Setup</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                  <SelectItem value="callback_scheduled">Callback Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Call Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Call Timeline ({total.toLocaleString()} records)</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {calls.map((call: any) => (
                    <div 
                      key={call.id} 
                      className={`p-4 rounded-lg border-l-4 bg-muted/30 ${outcomeColors[call.call_outcome] || 'border-l-gray-500'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-1">
                            {outcomeIcons[call.call_outcome] || <Phone className="h-4 w-4" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{call.debtor_name}</span>
                              <span className="text-xs text-muted-foreground font-mono">{call.account_number}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span className="capitalize">{call.call_outcome.replace(/_/g, ' ')}</span>
                              <span>•</span>
                              <span>{formatDuration(call.call_duration_seconds)}</span>
                              {call.is_right_party_contact === 1 && (
                                <>
                                  <span>•</span>
                                  <span className="text-emerald-500">RPC</span>
                                </>
                              )}
                              {call.is_promise_to_pay === 1 && (
                                <>
                                  <span>•</span>
                                  <span className="text-amber-500">PTP</span>
                                </>
                              )}
                            </div>
                            {(call.settlement_offered_amount || call.settlement_accepted_amount) && (
                              <div className="mt-2 text-sm">
                                {call.settlement_offered_amount && (
                                  <span className="text-muted-foreground">Offered: {formatCurrency(call.settlement_offered_amount)}</span>
                                )}
                                {call.settlement_accepted_amount && (
                                  <span className="text-emerald-500 ml-3">Accepted: {formatCurrency(call.settlement_accepted_amount)}</span>
                                )}
                              </div>
                            )}
                            {call.notes && (
                              <p className="mt-2 text-sm text-muted-foreground">{call.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground shrink-0">
                          <div>{new Date(call.call_date).toLocaleDateString()}</div>
                          <div>{new Date(call.call_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total.toLocaleString()} records
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm px-2">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
